<template>
  <div class="space-y-12">
    <div class="grid grid-cols-2 mb-[45px]">
      <div>
        <h1 class="text-5xl font-medium text-zinc-800">{{ __('Members') }}</h1>
      </div>
      <div class="text-right">
        <Button
            :content="__('Invite new member')"
            variation="fill"
            icon="contract_edit"
            @click="goToInvitations"
        />
      </div>
    </div>


    <div class="space-y-8">
      <SectionHeader :title="__('Company Team Members')">
        <template #icon>
          <Icon class="text-3xl text-primary" name="assignment_ind" is-filled />
        </template>
      </SectionHeader>

      <TabGroup :selectedIndex="selectedTab" @change="changeTab">
        <TabList class="flex gap-2 rounded-lg bg-white p-3.5">
          <Tab v-slot="{ selected }" as="template">
            <button :class="`${selected ? 'bg-secondary/5' : 'bg-white'} grow rounded-md px-8 py-3 font-medium capitalize leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5`" content="">
              {{ __('Members') }}
            </button>
          </Tab>
          <Tab v-slot="{ selected }" as="template">
            <button :class="`${selected ? 'bg-secondary/5' : 'bg-white'} grow rounded-md px-8 py-3 font-medium capitalize leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5`" content="">
              {{ __('Invitations') }}
            </button>
          </Tab>
        </TabList>
        <TabPanels>

          <div class="grid gap-x-4 gap-y-2 rounded-lg bg-white p-6" :class="selectedTab === 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'">

            <template v-if="filters?.search?.show">
              <InputText
                  id="search"
                  :placeholder="__('Search recruiter')"
                  :value="filters?.search?.value"
                  clearable
                  @update:value="handleSearchUpdate"
              />
            </template>

            <template v-if="filters?.active?.show && selectedTab === 0">
              <InputMultiSelect
                  class="z-40"
                  :placeholder="__('Status')"
                  :values="filters.active.value"
                  :options="filters.active.options"
                  @update:values="handleStatusUpdate"
              />
            </template>

            <template v-if="filters?.type?.show">
              <InputMultiSelect
                  class="z-40"
                  :placeholder="__('Recruiter role')"
                  :values="filters.type.value"
                  :options="filters.type.options"
                  @update:values="handleTypeUpdate"
              />
            </template>

          </div>

          <TabPanel>

            <MembersList
                v-model:members="members"
                :options="options"
                :user="user"
                :role="role"
                @update="handleUpdateButtonClick"
                @delete="handleDeleteMember"
            />

            <Pagination
                class="mt-6"
                :page="pagination.page"
                :pages="pagination.total"
                @update:page="handlePageChange"
            />
          </TabPanel>


          <TabPanel>
            <InvitationsList
                v-model:invitations="invitations"
                v-model:members="members"
                :options="options"
                :user="user"
                :role="role"
                @updateInvitedMembers="handleUpdateInvitedMembers"
            />
          </TabPanel>


        </TabPanels>
      </TabGroup>







    </div>
  </div>
</template>

<script setup>
import {ref, watch} from 'vue';
  import { Icon } from '@/components/block/icon';
  import { SectionHeader } from '@/pages/components/section-header';
  import { Pagination } from "@/components/block/pagination";
  import axios from "axios";
  import {__} from "@/helpers";
  import { InputMultiSelect } from "@/components/form/input-multi-select";
  import { InputText } from "@/components/form/input-text";
  import { Button } from "@/components/global";
  import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue";
  import { MembersList } from "@/pages/company/members/components/member";
  import { InvitationsList } from "@/pages/company/members/components/invite";
  import { useToast } from "vue-toastification";

  const props = defineProps({
    members: {
      type: Array,
      required: true
    },
    invitations: {
      type: Array,
      required: true
    },
    options: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    role: {
      type: String,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      required: true
    }
  });

  const pagination = ref(props.pagination);
  const filters = ref(props.filters);
  const selectedTab = ref(0);
  const toast = useToast();
  const members = ref(props.members);
  const invitations = ref(props.invitations);
  const emits = defineEmits(['update']);
  function changeTab(index) {
    selectedTab.value = index
  }

  function goToInvitations() {
    selectedTab.value = 1
  }

  function handleUpdateButtonClick(member) {
    members.value = members.value.map((item) => {
      if (item.id === member.id) {
        return member;
      }

      return item;
    });
  }

  function handleDeleteMember(member) {
    members.value = members.value.filter((item) => item.id !== member.id);
  }

  function handleUpdateInvitedMembers(invitation) {
    invitations.value.push(invitation);
  }

  watch(selectedTab, (value) => {
    const url = new URL(window.location.href);
    if (value === 0) {
      url.searchParams.set('tab', 'members');
     console.log('the tab is members')
    } else {
      url.searchParams.set('tab', 'invitations');
    }
  })
  async function handlePageChange(index) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', index);
    window.history.replaceState({}, '', url);

    await fetchMembers(url.searchParams.toString());
  }

  async function fetchMembers(params) {
    const baseUri = route('redesign.company.members.load');
    const uri = params ? `${baseUri}?${params}` : baseUri;

    try {
      const response = await axios.get(uri);
      members.value = response.data.members;
      invitations.value = response.data.invitations;
      pagination.value = response.data.pagination;
      filters.value = response.data.filters;

    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(__('An unexpected error has occured. Please try again later.'));
      }
    }

  }

  let debounceTimeout;
  async function handleTypeUpdate(values) {
    const url = new URL(window.location.href);
    url.searchParams.delete('type[]');
    values.forEach((value) => {
      url.searchParams.append('type[]', value.id);
    });
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchMembers(url.searchParams.toString());
  }

  async function handleStatusUpdate(values) {
    const url = new URL(window.location.href);
    url.searchParams.delete('active[]');
    values.forEach((value) => {
      url.searchParams.append('active[]', value.id);
    });
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchMembers(url.searchParams.toString());
  }

  async function handleSearchUpdate(value) {
    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(async () => {
      const url = new URL(window.location.href);

      if (!value) {
        url.searchParams.delete('search');
      } else {
        url.searchParams.set('search', value);
      }

      url.searchParams.delete('page');
      window.history.replaceState({}, '', url);

      await fetchMembers(url.searchParams.toString());
    }, 500);
  }

</script>
